import _ from 'lodash';
import React, { useEffect } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import styled, { css } from 'styled-components';

import { CarouselData } from '../types';

import SlideSelector from './SlideSelector';

const AUTOPLAY_INTERVAL = 6000;
const AMOUNT_OF_STARS = 5;

type Props = {
  isMobile: boolean;
  isDesktop: boolean | undefined;
  slides: CarouselData[];
};

const ShidoIsTheBestAssistantCarousel = ({
  isMobile,
  isDesktop,
  slides,
}: Props) => {
  const { scrollRef, activePageIndex, pages, snapPointIndexes, goTo } =
    useSnapCarousel();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (activePageIndex === pages.length - 1) {
        goTo(0);
      } else {
        goTo(activePageIndex + 1);
      }
    }, AUTOPLAY_INTERVAL);

    return () => {
      clearTimeout(timeout);
    };
  }, [activePageIndex, pages, goTo]);

  return (
    <Container $isDesktop={isDesktop}>
      <Scroll ref={scrollRef}>
        {slides.map((item, i) => (
          <Content key={item.id} $isSnapPoint={snapPointIndexes.has(i)}>
            <StarRatingContainer $isMobile={isMobile}>
              {_.range(AMOUNT_OF_STARS).map((number: number) => (
                <Star key={number} src="/star.svg" alt="" />
              ))}
            </StarRatingContainer>
            <Text>{item.text}</Text>
            <Image
              $isSpecial={i === 1}
              $isMobile={isMobile}
              src={item.imageSrc}
              alt={item.name}
            />
            <Name>{item.name}</Name>
          </Content>
        ))}
      </Scroll>
      <SelectorContainer aria-hidden $isMobile={isMobile}>
        <SlideSelector
          slides={slides}
          activeIdx={activePageIndex}
          interval={AUTOPLAY_INTERVAL}
          clickHandler={goTo}
        />
      </SelectorContainer>
    </Container>
  );
};

const Container = styled.div<{
  $isDesktop?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 42px;
  ${({ $isDesktop }) =>
    $isDesktop &&
    css`
      margin-top: 43px;
    `};

  @media (width <= 600px) {
    margin-top: 38px;
  }
`;

const SelectorContainer = styled.div<{ $isMobile?: boolean }>`
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      margin-top: 0;
    `};
`;

const Scroll = styled.ul`
  position: relative;
  display: flex;
  overflow: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  box-sizing: border-box;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Content = styled.div<{ $isSnapPoint: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 100%;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  ${({ $isSnapPoint }) =>
    $isSnapPoint &&
    css`
      scroll-snap-align: start;
    `}

  p {
    text-align: center;
  }
`;

const StarRatingContainer = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-bottom: 35px;

  @media (width <= 600px) {
    padding-bottom: 13px;
  }

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      @media (width <= 900px) {
        padding-bottom: 13px;
      }
    `}
`;

const Star = styled.img`
  width: 24px;
  height: 24px;

  &:not(:last-child) {
    margin-right: 5px;
  }
`;

const Image = styled.img<{ $isMobile?: boolean; $isSpecial?: boolean }>`
  width: 61px;
  height: 61px;
  border-radius: 50%;
  margin-bottom: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 41px;
    height: 41px;
    margin-top: 23px;
    margin-bottom: 14px;
  }

  ${({ $isSpecial }) =>
    $isSpecial &&
    css`
      @media (width <= 600px) {
        margin-top: -2px;
      }
    `}

  ${({ $isSpecial, $isMobile }) =>
    $isSpecial &&
    $isMobile &&
    css`
      @media (width <= 900px) {
        margin-top: 0;
      }
    `}
`;

const Name = styled.p`
  font-size: 16px;
  font-weight: 400;

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 12px;
    width: 150px;
    margin: 0 auto;
  }
`;

const Text = styled.p`
  font-size: 20px;
  font-weight: 300;
  white-space: pre-line;
  line-height: 30px;
  font-style: italic;
  padding-right: 1px;
  margin-bottom: 24px;

  ${({ theme }) => theme.breakpoints.down('lg')} {
    font-size: 18px;
    margin-bottom: 10px;
    line-height: 21px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 14px;
    width: 100vw;
    letter-spacing: -0.5px;
    padding: 0 10px;
    box-sizing: border-box;
  }

  @media (width <= 450px) {
    letter-spacing: normal;
    padding: 0;
  }

  @media (width <= 360px) {
    font-size: 12px;
    letter-spacing: -0.5px;
  }

  @media (width <= 319px) {
    font-size: 11px;
  }
`;

export default ShidoIsTheBestAssistantCarousel;
